import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BookText, ListChecks, ClipboardCheck, Mic, Leaf, Wallet as WalletIcon, Power, Sparkles, Check } from "lucide-react";

import { useAuth } from "../AuthContext";
import { t } from "../i18n";
import { formatPhone } from "../utils/phone";
import LanguageSelector from "../components/LanguageSelector";
import AnimatedFeatureIcon from "../components/AnimatedFeatureIcon";

const FEATURE_BASE = [
  { type: "summary",       bg: "bg-amber-300",   icon: BookText,       titleKey: "lang_summary",       descKey: "lang_summary_desc",       priceKey: "summary_price",       defaultCost: 2 },
  { type: "speech-mentor", bg: "bg-sky-300",     icon: Mic,            titleKey: "lang_speech_mentor", descKey: "lang_speech_mentor_desc", priceKey: "speech_mentor_price", defaultCost: 3 },
  { type: "mcq",           bg: "bg-violet-300",  icon: ListChecks,     titleKey: "lang_mcq",           descKey: "lang_mcq_desc",           priceKey: "mcq_price",           defaultCost: 2 },
  { type: "check-answer",  bg: "bg-emerald-300", icon: ClipboardCheck, titleKey: "lang_check_answer", descKey: "lang_check_answer_desc",  priceKey: "check_answer_price", defaultCost: 2 },
  { type: "learn-nature",  bg: "bg-lime-300",    icon: Leaf,           titleKey: "lang_learn_nature", descKey: "lang_learn_nature_desc",  priceKey: "learn_nature_price", defaultCost: 3 },
];

const AGE_GROUPS = [
  { key: "6-10 years",  labelKey: "age_6_10"  },
  { key: "10-14 years", labelKey: "age_10_14" },
  { key: "14-18 years", labelKey: "age_14_18" },
  { key: "18+ years",   labelKey: "age_18_plus" },
];

export default function Language() {
  const navigate = useNavigate();
  const { user, lang, config, logout } = useAuth();
  const balance = user?.wallet_balance ?? 0;
  const currency = config?.currency_symbol || "₹";
  const features = FEATURE_BASE.map((f) => ({ ...f, cost: config?.[f.priceKey] ?? f.defaultCost }));

  const [ageGroup, setAgeGroup] = useState(
    () => sessionStorage.getItem("dt_lang_age") || "10-14 years"
  );

  const onPickFeature = (type) => {
    sessionStorage.setItem("dt_lang_age", ageGroup);
    if (type === "speech-mentor") {
      navigate("/language/speech-mentor");
      return;
    }
    navigate(`/language/capture/${type}`);
  };

  return (
    <div className="page-shell" data-testid="language-page">
      <div className="flex items-center justify-between gap-3">
        <button
          data-testid="back-home-btn"
          onClick={() => navigate("/")}
          className="p-2 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
          aria-label="back"
        >
          <ArrowLeft size={18} strokeWidth={2.5} className="text-slate-700" />
        </button>
        <button
          data-testid="wallet-pill"
          onClick={() => navigate("/wallet")}
          className="flex items-center gap-2 bg-emerald-400 hover:bg-emerald-500 text-white px-4 py-2 rounded-full font-bold shadow-[0_4px_0_rgba(0,0,0,0.1)] border-2 border-emerald-600/20 active:translate-y-1 active:shadow-none transition-all"
        >
          <WalletIcon size={18} strokeWidth={2.5} />
          {currency}<span data-testid="wallet-balance">{balance}</span>
        </button>
        <div className="flex items-center gap-2">
          <LanguageSelector compact />
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="p-2 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
            aria-label="logout"
          >
            <Power size={18} strokeWidth={2.5} className="text-slate-600" />
          </button>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 text-center">
        <div className="inline-flex items-center gap-2 bg-white px-4 py-1 rounded-full border-2 border-slate-900/10 text-sm font-bold text-slate-600">
          <Sparkles size={16} strokeWidth={2.5} className="text-violet-500" /> {t(lang, "welcome")}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-3 text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">
          {t(lang, "module_language_title")}
        </h1>
        <p className="text-base sm:text-lg text-cyan-50/95 mt-1 font-semibold drop-shadow-[0_2px_8px_rgba(15,23,42,0.45)]">
          {t(lang, "language_tagline")}
        </p>
      </motion.div>

      {/* Age group selector */}
      <div className="mt-6 bg-white rounded-3xl p-4 border-4 border-white shadow-[0_6px_0_rgba(0,0,0,0.06)]" data-testid="age-group-card">
        <h3 className="font-heading text-base font-bold text-slate-700 mb-3">
          {t(lang, "pick_age_group")}
        </h3>
        <div className="grid grid-cols-2 gap-2.5">
          {AGE_GROUPS.map((a) => {
            const selected = a.key === ageGroup;
            return (
              <button
                key={a.key}
                role="radio"
                aria-checked={selected}
                data-testid={`age-${a.key.replace(/[^a-z0-9]/gi, "_")}`}
                onClick={() => setAgeGroup(a.key)}
                className={
                  "relative px-3 py-3 rounded-2xl border-2 text-sm font-bold leading-tight text-left transition-all " +
                  (selected
                    ? "bg-violet-500 text-white border-violet-700 shadow-[0_3px_0_rgba(0,0,0,0.18)]"
                    : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100")
                }
              >
                <span className="block">{t(lang, a.labelKey)}</span>
                {selected && (
                  <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white flex items-center justify-center">
                    <Check size={12} strokeWidth={3.5} className="text-violet-600" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-5">
        {features.map((f, i) => {
          return (
            <motion.button
              key={f.type}
              data-testid={`feature-lang-${f.type}-btn`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * i }}
              whileTap={{ scale: 0.97 }}
              onClick={() => onPickFeature(f.type)}
              className={`relative w-full ${f.bg} rounded-3xl p-5 text-left card-squish border-4 border-white flex items-center gap-4`}
            >
              <div className="w-16 h-16 rounded-2xl bg-white/90 flex items-center justify-center shadow-inner">
                <AnimatedFeatureIcon type={f.type} icon={f.icon} size={32} className="text-slate-800" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-2xl font-bold text-slate-800 truncate">
                  {t(lang, f.titleKey)}
                </h3>
                <p className="text-sm text-slate-700 font-semibold mt-0.5 truncate">
                  {t(lang, f.descKey)}
                </p>
              </div>
              <div className="shrink-0 px-3 py-1.5 rounded-full font-heading font-bold text-base bg-white text-slate-800 border-2 border-slate-900/10">
                {currency}{f.cost}
              </div>
            </motion.button>
          );
        })}
      </div>

      <p className="mt-8 text-center text-xs text-cyan-100/80 font-semibold drop-shadow-[0_2px_6px_rgba(15,23,42,0.4)]">
        {formatPhone(user?.phone)}
      </p>
    </div>
  );
}
